import Konva from 'konva';
import React from 'react';
import { useEffect, useRef } from 'react';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { drawTableGrid, addTableCell } from './soil-survey-header';
import {
    drawPOValues,
    drawSIValues,
    drawPAValues,
    drawGraphGrid,
    drawDepthBar,
    drawHMLabels,
    drawNONEValues,
    drawCUValues,
    drawGridLines,
    calculateValues,
    drawHPValues,
    drawHEValues,
} from './soil-survey-graph';
import { ExtraData, HeaderData, Measurement, SoilSurveyContentProps } from './soil-survey-types';
import { SURVEY_TYPE_TITLES } from './soil-survey-constants';

const currentSoil = { value: '' };

const SoilSurveyContent = ({ headerData, measurements }: SoilSurveyContentProps) => {
    const canvasRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<Konva.Stage | null>(null);
    const activeTooltipsRef = useRef<HTMLElement[]>([]);

    useEffect(() => {
        if (!canvasRef.current) return;

        // Clean up previous tooltips
        activeTooltipsRef.current.forEach(tooltip => {
            if (tooltip.parentNode) {
                tooltip.parentNode.removeChild(tooltip);
            }
        });
        activeTooltipsRef.current = [];

        // Account for padding and use available space
        const containerWidth = Math.max(canvasRef.current.clientWidth - 20, 700);
        const containerHeight = Math.max(canvasRef.current.clientHeight - 20, 800);

        const stage = new Konva.Stage({
            container: canvasRef.current,
            width: containerWidth,
            height: containerHeight,
        });
        stageRef.current = stage;

        const layer = new Konva.Layer();
        stage.add(layer);
        drawSoilSurvey(layer, stage, headerData, measurements, activeTooltipsRef.current);
        layer.draw();

        // Cleanup function
        return () => {
            activeTooltipsRef.current.forEach(tooltip => {
                if (tooltip.parentNode) {
                    tooltip.parentNode.removeChild(tooltip);
                }
            });
            activeTooltipsRef.current = [];
            if (stageRef.current) {
                stageRef.current.destroy();
                stageRef.current = null;
            }
        };
    }, [headerData, measurements]);

    return (
        <div
            ref={canvasRef}
            style={{
                width: '100%',
                height: '100%',
                minWidth: '700px',
                minHeight: '800px',
                padding: '10px',
                boxSizing: 'border-box',
                overflow: 'hidden',
                backgroundColor: 'white',
            }}
        />
    );
};

const drawSoilSurvey = (layer: Konva.Layer, stage: Konva.Stage, data: HeaderData, measurements: Measurement[], activeTooltips: HTMLElement[]) => {
    const headerHeight = 120;
    const margin = 20;
    const offset = 30;
    const headerBorder = new Konva.Rect({
        x: margin,
        y: margin,
        width: stage.width() - 2 * margin,
        height: headerHeight,
        stroke: 'black',
        strokeWidth: 1,
    });
    layer.add(headerBorder);

    const tableY = margin;
    const colWidth = (stage.width() - 2 * margin) / 4;

    drawTableGrid(layer, margin, tableY, colWidth, 90);

    const fontSize = 12;
    const padding = 5;

    // Row 1
    addTableCell(layer, 'KOHDE', margin + padding, tableY + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.target, margin + padding + 2 * offset, tableY + padding, fontSize, 'black', colWidth - 2 * offset - padding, activeTooltips);
    addTableCell(layer, 'SUUNT', margin + 2 * colWidth + padding, tableY + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, 'TNO', margin + 3 * colWidth + padding, tableY + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.tno, margin + 3 * colWidth + offset + padding, tableY + padding, fontSize, 'black', colWidth - offset - padding, activeTooltips);

    // Row 2
    addTableCell(layer, 'PAIKKA', margin + padding, tableY + offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.location, margin + padding + 2 * offset, tableY + offset + padding, fontSize, 'black', colWidth - 2 * offset - padding, activeTooltips);

    addTableCell(layer, 'X', margin + 2 * colWidth + padding, tableY + offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.x.toString(), margin + 2 * colWidth + offset + padding, tableY + offset + padding, fontSize, 'black', colWidth - offset - padding, activeTooltips);
    addTableCell(layer, 'Y', margin + 3 * colWidth + padding, tableY + offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.y.toString(), margin + 3 * colWidth + offset + padding, tableY + offset + padding, fontSize, 'black', colWidth - offset - padding, activeTooltips);

    // Row 3
    addTableCell(layer, 'TUTK', margin + padding, tableY + 2 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, 'TUTKPVM', margin + colWidth + padding, tableY + 2 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.date, margin + colWidth + 2 * offset + padding, tableY + 2 * offset + padding, fontSize, 'black', undefined, activeTooltips);
    addTableCell(layer, 'MAANPINTA', margin + 2 * colWidth + padding, tableY + 2 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.surface, margin + 2 * colWidth + 3 * offset + padding, tableY + 2 * offset + padding, fontSize, 'black', undefined, activeTooltips);
    addTableCell(layer, 'PÄÄTTYMINEN', margin + 3 * colWidth + padding, tableY + 2 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.endPoint, margin + 3 * colWidth + 3 * offset + padding, tableY + 2 * offset + padding, fontSize, 'black', undefined, activeTooltips);

    // Bottom info
    addTableCell(layer, data.title, margin + padding, tableY + 3 * offset + padding, fontSize, 'black', undefined, activeTooltips);
    addTableCell(layer, 'PVM', margin + 2 * colWidth + padding, tableY + 3 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.currentDate, margin + 2 * colWidth + offset + padding, tableY + 3 * offset + padding, fontSize, 'black', undefined, activeTooltips);
    addTableCell(layer, 'MK', margin + 3 * colWidth + padding, tableY + 3 * offset + padding, fontSize, 'gray', undefined, activeTooltips);
    addTableCell(layer, data.mk, margin + 3 * colWidth + offset + padding, tableY + 3 * offset + padding, fontSize, 'black', undefined, activeTooltips);

    // Separate HM measurements from regular measurements
    const hmMeasurements: Measurement[] = [];
    const filteredMeasurements: Measurement[] = [];

    for (const measurement of measurements) {
        if (measurement.isHM) {
            hmMeasurements.push(measurement);
        } else {
            filteredMeasurements.push(measurement);
        }
    }

    const startY = headerHeight + margin * 2;
    const depthBarStartY = headerHeight + margin + 20;

    const surveyCode = Object.keys(SURVEY_TYPE_TITLES).find(key => SURVEY_TYPE_TITLES[key] === data.title) || data.title;

    switch (surveyCode) {
        case 'PA':
        case 'WST':
            drawPAValues(layer, stage, filteredMeasurements, startY, data.alValue || 0);
            break;
        case 'SI':
        case 'FVT':
            drawSIValues(layer, stage, filteredMeasurements, startY);
            break;
        case 'PO':
            drawPOValues(layer, stage, filteredMeasurements, startY, data.alValue || 0);
            break;
        case 'KO':
            break;
        case 'NE':
            drawNONEValues(layer, stage, filteredMeasurements, startY);
            break;
        case 'NO':
            drawNONEValues(layer, stage, filteredMeasurements, startY);
            break;
        case 'CU':
        case 'CPTU':
            drawCUValues(layer, stage, filteredMeasurements, startY, data.alValue || 0);
            break;
        case 'TR':
        case 'PI':
            break;
        case 'HE':
            drawHEValues(layer, stage, filteredMeasurements, startY, data.alValue || 0);
            break;
        case 'HP':
            drawHPValues(layer, stage, filteredMeasurements, startY, data.alValue || 0);
            break;
        default:
            drawWorkInProcessText(layer, stage, data.title);
            return;
    }

    drawMeasurementGridLines(layer, stage, filteredMeasurements, startY, surveyCode);
    drawGraphGrid(layer, stage, filteredMeasurements, startY, surveyCode);
    const alData = { alValue: data.alValue ? data.alValue : 0, alSoilType: data.alSoilType ? data.alSoilType : '' };
    drawDepthBar(layer, stage, filteredMeasurements, depthBarStartY, data.surface, surveyCode, alData, currentSoil, data.alType);
    if (hmMeasurements.length > 0 || data.hmText) {
        drawHMLabels(layer, stage, hmMeasurements, filteredMeasurements, startY, surveyCode, data.hmText || '');
    }
};

export const showSoilSurveyPopup = (headerData: HeaderData, measurements: Measurement[]) => {
    const windowId = `soil-survey-${headerData.tno}-${headerData.x}-${headerData.y}`;

    WindowManager.open({
        group: 'soil-survey',
        id: windowId,
        title: `${headerData.title} (${headerData.tno})`,
        allowMultiple: true,
        showCloseButton: true,
        defaultSizing: {
            width: 720,
            height: 820,
        },
        body: <SoilSurveyContent headerData={headerData} measurements={measurements} />,
    });
};

export const showTekPointSoilSurvey = (pointData: any) => {
    const [x, y, surface, date, location] = prepareHeading(pointData['XY']);
    const { measurements, endPoint } = parseDataAndCalculateEndPoint(pointData, surface);
    const alkuData = parseAlkukairaustiedot(pointData.alkukairaustiedot);

    const headerData: HeaderData = {
        tno: (pointData.työnumero?.split(/\s+/)[0] || '').trim(),
        title: SURVEY_TYPE_TITLES[pointData.title] || pointData.title || '',
        target: '', // TODO: figure out what this is
        x,
        y,
        date,
        location: location === '-' ? '' : location,
        surface,
        endPoint,
        currentDate: new Date().toLocaleDateString('fi-FI'),
        mk: '',
        alType: alkuData.alType,
        alValue: alkuData.alValue,
        alSoilType: alkuData.alSoilType,
        hmText: pointData.huomautustekstit || '',
    };

    showSoilSurveyPopup(headerData, measurements);
};

const parseAlkukairaustiedot = (alkukairaustiedot: string) => {
    if (!alkukairaustiedot) {
        return { alValue: 0, alType: '', alSoilType: '' };
    }

    const tabParts = alkukairaustiedot.split('\t');
    const value = tabParts[0]?.trim() || '';
    const type = tabParts[1]?.trim() || '';
    const soilType = tabParts[2]?.trim() || '';

    return {
        alValue: parseFloat(value) || 0,
        alType: type,
        alSoilType: soilType,
    };
};

const prepareHeading = (heading: string): [number?, number?, string?, string?, string?] => {
    const values = heading.split(/\s+/);
    const dateStr = values[3];
    let formattedDate = '';

    if (dateStr && dateStr.length === 8) {
        const day = dateStr.substring(0, 2);
        const month = dateStr.substring(2, 4);
        const year = dateStr.substring(4, 8);
        formattedDate = `${day}.${month}.${year}`;
    }

    return [+values[0], +values[1], values[2], formattedDate, values.slice(4).join(' ') || undefined];
};

const parseDataAndCalculateEndPoint = (pointData: any, maanpinta: string): { measurements: Measurement[]; endPoint: string } => {
    const measurements = [];
    let maxDepth = 0;
    const tt = pointData['tutkimustapa'].split(/\s+/)[0];

    for (const key in pointData) {
        if (!isNaN(Number(key))) {
            const raw = pointData[key];
            const parts = raw.split(/\s+/);

            if (parts[0] === 'HM') {
                const hmText = parts.slice(1).join(' ');
                measurements.push({
                    depth: measurements.length > 0 ? measurements[measurements.length - 1].depth : 0,
                    soilType: '',
                    isHM: true,
                    hmText: hmText,
                    extraData: [],
                });
            }

            if (parts[0] === 'LB' || parts[0] === 'EM' || parts[0] === 'RK') {
                if (measurements.length > 0 && !measurements[measurements.length - 1].isHM) {
                    if (!measurements[measurements.length - 1].extraData) {
                        measurements[measurements.length - 1].extraData = [];
                    }
                    measurements[measurements.length - 1].extraData.push({
                        type: parts[0],
                        data: parts.slice(1).join(' '),
                    });
                }
                continue;
            }

            let depth = 0;
            let par1 = null;
            let par2 = null;
            let par3 = null;
            let par4 = null;
            let soilType = '';
            const extras: ExtraData[] = [];

            if (tt === 'PO') {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                soilType = parts.slice(2).find(part => part.length > 0) || '';
            } else if (tt === 'HP') {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                par2 = parseFloat(parts[2]);
                if (parts[3]) {
                    extras.push({ type: 'HP', data: parts[3] });
                }
                soilType = parts.slice(4).find(part => part.length > 0) || '';
            } else if (tt === 'CU' || tt === 'CPTU') {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                par2 = parseFloat(parts[2]);
                par3 = parseFloat(parts[3]);
                par4 = parseFloat(parts[4]);
                soilType = parts.slice(5).find(part => part.length > 0) || '';
            } else if (tt === 'KO') {
                depth = parseFloat(parts[0]);
                soilType = parts.slice(1).find((part: string) => part.length > 0) || '';
            } else if (tt === 'TR' || tt === 'PI') {
                depth = parseFloat(parts[0]);
                soilType = parts.slice(1).find(part => part.length > 0) || '';
            } else if (tt === 'SI') {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                par2 = parseFloat(parts[2]);
                par3 = parseFloat(parts[3]);
                par4 = parseFloat(parts[4]);
            } else if (tt === 'NE' || tt === 'NO') {
                depth = (parseFloat(parts[0]) + parseFloat(parts[2])) / 2;
                par1 = parseFloat(parts[1]);
                par2 = parseFloat(parts[2]);
                soilType = parts.slice(3).find(part => part.length > 0) || '';
            } else if (tt === 'KE') {
                depth = (parseFloat(parts[0]) + parseFloat(parts[1])) / 2;
            } else if (tt === 'HE') {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                soilType = parts.slice(2).find(part => part.length > 0) || '';
            } else {
                depth = parseFloat(parts[0]);
                par1 = parseFloat(parts[1]);
                par2 = parseFloat(parts[2]);
                par3 = parseFloat(parts[3]);
                soilType = parts.slice(3).find(part => isNaN(parseFloat(part))) || '';
            }

            if (!isNaN(depth) && (!isNaN(par1) || par1 === null) && (!isNaN(par2) || par2 === null)) {
                measurements.push({
                    depth,
                    value1: par1,
                    value2: par2,
                    value3: par3,
                    value4: par4,
                    soilType,
                    extraData: extras,
                });

                if (depth > maxDepth) {
                    maxDepth = depth;
                }
            }
        }
    }

    measurements.sort((a, b) => a.depth - b.depth);
    const startingLevel = parseFloat(maanpinta) || 0;

    return {
        measurements,
        endPoint: (startingLevel - Math.max(...measurements.map(m => m.depth))).toFixed(2),
    };
};

const drawWorkInProcessText = (layer: Konva.Layer, stage: Konva.Stage, tt: string) => {
    const label = new Konva.Text({
        x: 0,
        y: stage.height() / 4,
        text: tt.toString() + ' tutkimustavasta ei ole (vielä) kaaviota',
        fontSize: 15,
        fontFamily: 'Arial',
        fill: 'black',
        width: stage.width(),
        align: 'center',
    });
    layer.add(label);
};

export const drawMeasurementGridLines = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, tt: string) => {
    const values = calculateValues(tt, stage, startY, measurements);
    const { barX, barY, barWidth, barHeight, maxDepth, stepSize, maxLeftLength, maxRightLength } = values;

    let drawLeft = false;
    let drawRight = false;

    switch (tt) {
        case 'PA':
        case 'WST':
            drawLeft = true;
            drawRight = true;
            break;
        case 'SI':
        case 'FVT':
            drawLeft = false;
            drawRight = true;
            break;
        case 'PO':
            drawLeft = true;
            drawRight = false;
            break;
        case 'TR':
            drawLeft = false;
            drawRight = false;
            break;
        case 'NE':
        case 'NO':
            drawLeft = maxLeftLength > 0;
            drawRight = maxRightLength > 0;
            break;
        case 'CU':
        case 'CPTU':
            drawLeft = maxLeftLength > 0;
            drawRight = maxRightLength > 0;
            break;
        case 'HP':
            drawLeft = true;
            drawRight = true;
            break;
        case 'HE':
            drawRight = true;
            drawLeft = false;
            break;
        default:
            break;
    }

    drawGridLines({
        layer,
        barX,
        barY,
        barWidth,
        barHeight,
        maxDepth,
        stepSize,
        maxLeftLength,
        maxRightLength,
        drawLeft,
        drawRight,
    });
};
